import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  ArrowLeft, 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  Edit, 
  Trash2,
  Eye,
  Copy,
  Lock
} from 'lucide-react';
import { theme } from '../../styles/theme';
import { Card } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input } from '../../components/UI/Input';
import { Modal } from '../../components/UI/Modal';
import { useApiOnMount, useApi } from '../../hooks/useApi';
import { spaceService } from '../../services/spaceService';
import { secretService } from '../../services/secretService';
import { formatRelativeTime, getSecretTypeIcon, truncateText } from '../../utils/helpers';
import LabelSelector from '../../components/UI/LabelSelector';
import SecretCard from '../../components/UI/SecretCard';
import toast from 'react-hot-toast';

const SpaceDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const BackButton = styled(Button)`
  padding: ${({ theme }) => theme.spacing.sm};
`;

const SpaceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SpaceColor = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color || theme.colors.primary};
  flex-shrink: 0;
`;

const SpaceDetails = styled.div`
  h1 {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text};
    margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  }
  
  p {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.textSecondary};
    margin: 0;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

const SearchInput = styled(Input)`
  padding-left: ${({ theme }) => theme.spacing['2xl']};
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.gray400};
  z-index: 1;
`;

const FilterButton = styled(Button)`
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: none;
  }
`;

const SecretsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
`;



const LabelsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const Label = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background-color: ${({ $color }) => $color}20;
  color: ${({ $color }) => $color};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['3xl']};
  color: ${({ theme }) => theme.colors.textSecondary};
  
  h3 {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  }
  
  p {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  }
`;

const secretTypes = {
  login: 'Login',
  password: 'Password',
  api_key: 'API Key',
  note: 'Note',
  card: 'Card',
  ssh: 'SSH Key',
  database: 'Database',
  other: 'Other',
};

function SpaceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSecret, setEditingSecret] = useState(null);
  const [showActions, setShowActions] = useState(null);
  
  const { data: space, loading: spaceLoading, refetch: refetchSpace } = useApiOnMount(
    () => spaceService.getSpace(id)
  );
  const { data: secrets, loading: secretsLoading, refetch: refetchSecrets } = useApiOnMount(
    () => secretService.getSecrets(id)
  );
  const { execute: createSecret, loading: creating } = useApi(secretService.createSecret);
  const { execute: updateSecret, loading: updating } = useApi(secretService.updateSecret);
  const { execute: deleteSecret, loading: deleting } = useApi(secretService.deleteSecret);

  const filteredSecrets = secrets?.filter(secret =>
    secret.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    secret.description?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const handleCreateSecret = async (data) => {
    try {
      await createSecret(id, data);
      toast.success('Secret created successfully');
      setShowCreateModal(false);
      // Dispatch event to refresh label counts
      window.dispatchEvent(new CustomEvent('secretChanged'));
      refetchSecrets();
    } catch (error) {
      toast.error('Failed to create secret');
    }
  };

  const handleUpdateSecret = async (secretId, data) => {
    try {
      await updateSecret(secretId, data);
      toast.success('Secret updated successfully');
      setEditingSecret(null);
      // Dispatch event to refresh label counts
      window.dispatchEvent(new CustomEvent('secretChanged'));
      refetchSecrets();
    } catch (error) {
      toast.error('Failed to update secret');
    }
  };

  const handleDeleteSecret = async (secretId) => {
    if (window.confirm('Are you sure you want to delete this secret? This action cannot be undone.')) {
      try {
        await deleteSecret(secretId);
        toast.success('Secret deleted successfully');
        // Dispatch event to refresh label counts
        window.dispatchEvent(new CustomEvent('secretChanged'));
        refetchSecrets();
      } catch (error) {
        toast.error('Failed to delete secret');
      }
    }
  };

  const handleSecretClick = (secretId) => {
    navigate(`/secrets/${secretId}`);
  };

  const handleCopyPassword = async (secretId) => {
    try {
      const response = await secretService.getDecryptedPassword(secretId);
      await navigator.clipboard.writeText(response.data.password);
      toast.success('Password copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy password');
    }
  };

  const handleCopySecretKey = async (secretId) => {
    try {
      const response = await secretService.getDecryptedSecretKey(secretId);
      await navigator.clipboard.writeText(response.data.secret_key);
      toast.success('Secret key copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy secret key');
    }
  };

  if (spaceLoading) {
    return <div>Loading space...</div>;
  }

  if (!space) {
    return <div>Space not found</div>;
  }

  return (
    <SpaceDetailContainer>
      <Header>
        <HeaderLeft>
          <BackButton variant="ghost" onClick={() => navigate('/spaces')}>
            <ArrowLeft size={16} />
          </BackButton>
          <SpaceInfo>
            <SpaceColor $color={space.color} />
            <SpaceDetails>
              <h1>{space.name}</h1>
              <p>{space.description || 'No description'}</p>
            </SpaceDetails>
          </SpaceInfo>
        </HeaderLeft>
        <HeaderRight>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={16} />
            New Secret
          </Button>
        </HeaderRight>
      </Header>

      <Controls>
        <SearchContainer>
          <SearchIcon>
            <Search size={18} />
          </SearchIcon>
          <SearchInput
            placeholder="Search secrets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchContainer>
        <FilterButton variant="secondary">
          <Filter size={16} />
          Filter
        </FilterButton>
      </Controls>

      {secretsLoading ? (
        <div>Loading secrets...</div>
      ) : filteredSecrets.length > 0 ? (
        <SecretsGrid>
          {filteredSecrets.map((secret) => (
            <SecretCard
              key={secret.id}
              secret={secret}
              onSecretClick={handleSecretClick}
              onEdit={(secretId) => {
                const secret = filteredSecrets.find(s => s.id === secretId);
                setEditingSecret(secret);
              }}
              onDelete={handleDeleteSecret}
              onCopyPassword={handleCopyPassword}
              onCopySecretKey={handleCopySecretKey}
            />
          ))}
        </SecretsGrid>
      ) : (
        <EmptyState>
          <h3>No secrets found</h3>
          <p>
            {searchQuery 
              ? 'No secrets match your search criteria'
              : 'Create your first secret to start storing information'
            }
          </p>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={16} />
            Create Secret
          </Button>
        </EmptyState>
      )}

      {/* Create/Edit Secret Modal */}
      <SecretModal
        isOpen={showCreateModal || !!editingSecret}
        onClose={() => {
          setShowCreateModal(false);
          setEditingSecret(null);
        }}
        onSubmit={editingSecret ? 
          (data) => handleUpdateSecret(editingSecret.id, data) :
          handleCreateSecret
        }
        initialData={editingSecret}
        loading={creating || updating}
      />
    </SpaceDetailContainer>
  );
}

export default SpaceDetailPage;

// Secret Modal Component
function SecretModal({ isOpen, onClose, onSubmit, initialData, loading }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    link: '',
    domain: '',
    username: '',
    password: '',
    secret_key: '',
    type: 'login',
    label_ids: [],
    ...initialData
  });
  const [selectedLabelIds, setSelectedLabelIds] = useState(initialData?.labels?.map(l => l.id) || []);

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        description: initialData.description || '',
        link: initialData.link || '',
        domain: initialData.domain || '',
        username: initialData.username || '',
        password: '', // Don't pre-fill password for security
        secret_key: '', // Don't pre-fill secret key for security
        type: initialData.type || 'login',
        label_ids: initialData.labels?.map(l => l.id) || [],
      });
      setSelectedLabelIds(initialData.labels?.map(l => l.id) || []);
    } else {
      // Reset form for new secret
      setFormData({
        title: '',
        description: '',
        link: '',
        domain: '',
        username: '',
        password: '',
        secret_key: '',
        type: 'login',
        label_ids: [],
      });
      setSelectedLabelIds([]);
    }
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Filter out invalid label IDs (0, null, undefined, etc.)
    const validLabelIds = selectedLabelIds.filter(id => id && id > 0);
    onSubmit({ ...formData, label_ids: validLabelIds });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? 'Edit Secret' : 'Create New Secret'}
      maxWidth="600px"
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
        <Input
          label="Title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="Enter secret title"
          required
        />
        
        <Input
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Enter secret description (optional)"
        />
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
          <Input
            label="Link"
            name="link"
            value={formData.link}
            onChange={handleChange}
            placeholder="https://example.com"
            type="url"
          />
          
          <Input
            label="Domain"
            name="domain"
            value={formData.domain}
            onChange={handleChange}
            placeholder="example.com"
          />
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
          <Input
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            placeholder="Enter username"
          />
          
          <Input
            label="Password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Enter password"
            type="password"
          />
        </div>
        
        <Input
          label="Secret Key"
          name="secret_key"
          value={formData.secret_key}
          onChange={handleChange}
          placeholder="Enter secret key or API key"
        />
        
        <div>
          <label style={{ 
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium,
            color: theme.colors.gray700,
            marginBottom: theme.spacing.sm,
            display: 'block'
          }}>
            Type
          </label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: theme.spacing.sm,
              border: `1px solid ${theme.colors.gray300}`,
              borderRadius: theme.borderRadius.md,
              fontSize: theme.typography.fontSize.base,
            }}
          >
            {Object.entries(secretTypes).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        <LabelSelector
          selectedLabelIds={selectedLabelIds}
          onLabelsChange={setSelectedLabelIds}
          disabled={loading}
        />

        <div style={{ 
          display: 'flex', 
          gap: theme.spacing.md, 
          justifyContent: 'flex-end',
          marginTop: theme.spacing.lg
        }}>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading}
          >
            {initialData ? 'Update Secret' : 'Create Secret'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
